/**
 * demoIssues.js  (DECISIONS.md D255)
 * ──────────────────────────────────
 * Reports problems a DEMO visitor's browser runs into (JS errors, unhandled promise
 * rejections, screen crashes, failed API calls, timeouts) to POST /demo-sessions/issue/,
 * so the platform admin can see them per day on the Demo Leads tab.
 *
 * Does nothing outside a demo session: it only reports when localStorage.isDemo === 'true'
 * and a visitor email is stored under 'demoVisitor' (set by the demo gate form). Every
 * function here swallows its own errors -- reporting must never break the app itself.
 */

const BACKEND = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const SESSION_CAP = 30;          // max reports per page load (the server also caps per lead per day)
const DEDUPE_MS   = 10_000;      // identical report within this window is dropped

let _sent = 0;
const _recent = new Map();

function demoEmail() {
  try {
    if (window.localStorage.getItem('isDemo') !== 'true') return null;
    const v = JSON.parse(window.localStorage.getItem('demoVisitor') || 'null');
    return (v && v.email) ? String(v.email) : null;
  } catch {
    return null;
  }
}

export function reportDemoIssue({ kind, endpoint, status, message }) {
  try {
    const email = demoEmail();
    if (!email || _sent >= SESSION_CAP) return;

    const msg = String(message || '').slice(0, 500);
    const key = [kind, endpoint || '', status || '', msg.slice(0, 80)].join('|');
    const now = Date.now();
    if (_recent.get(key) && now - _recent.get(key) < DEDUPE_MS) return;
    _recent.set(key, now);
    _sent += 1;

    const payload = JSON.stringify({
      email,
      kind,
      page: window.location.pathname,
      endpoint: endpoint || null,
      status_code: status || null,
      message: msg || null,
    });
    const url = `${BACKEND}/demo-sessions/issue/`;
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([payload], { type: 'application/json' }));
      return;
    }
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* never let reporting break the app */
  }
}

/** Called from the axios error interceptor. Ignores expected/noisy cases. */
export function reportApiFailure(error) {
  try {
    if (!error || error.code === 'ERR_CANCELED') return;
    const url = (error.config && error.config.url) || '';
    if (url.includes('/demo-sessions/') || url.includes('/ping')) return;

    const status = error.response && error.response.status;
    if (status === 401) return;   // expected (session checks); real expiry is handled elsewhere

    let detail = '';
    const data = error.response && error.response.data;
    if (data && typeof data.detail === 'string') detail = data.detail;
    else if (data && typeof data.detail !== 'undefined') detail = JSON.stringify(data.detail);

    reportDemoIssue({
      kind: status ? 'api_error' : 'network_error',
      endpoint: url,
      status: status || null,
      message: detail || error.message || (status ? `HTTP ${status}` : 'Network error'),
    });
  } catch {
    /* ignore */
  }
}

/** Call once at startup (index.js). */
export function installDemoErrorCapture() {
  try {
    window.addEventListener('error', (e) => {
      // Harmless browser notices that are not real problems for the visitor.
      if (/ResizeObserver|^Script error\.?$/.test(String(e.message || ''))) return;
      const file = e.filename ? String(e.filename).split('/').pop() : '';
      reportDemoIssue({
        kind: 'js_error',
        message: `${e.message || 'Error'}${file ? ` @ ${file}:${e.lineno || ''}` : ''}`,
      });
    });
    window.addEventListener('unhandledrejection', (e) => {
      const r = e.reason;
      reportDemoIssue({
        kind: 'unhandled_rejection',
        message: (r && (r.message || String(r))) || 'Unhandled promise rejection',
      });
    });
  } catch {
    /* ignore */
  }
}
